import { observer } from 'mobx-react-lite';
import {Button, TextField, Checkbox, FormControlLabel } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useState } from 'react';
import { CopilotStore } from '../CopilotStore';
import { Box, Stack, Typography } from '@mui/material';
import useInterval from '../../../hooks/interval';
import { SelectionItem } from '../../controls/Autocomplete';
import { IngestFile } from '../models';
import StepConfirmation, { ConfirmationDialogProps } from '../../controls/StepConfirmation';

type StorageMigrationProps = {
    store: CopilotStore
}

interface StorageMigrationStatus {
    migrated: boolean;
    task_id: string;
  }
  
export const StorageMigration = observer(({ store }: StorageMigrationProps) => {

    const {
        selectedCustomer: customer,
        user_email,
        tagConfigs,
        fileById,
        filesByDocumentName,
        duplicateFilesByDocumentName,
        duplicateFiles,
    } = store

    const [isMigrated, setIsMigrated] = React.useState<boolean>();
    const [taskID, setTaskID] = React.useState<string>();
    const [aborted, setAborted] = React.useState<boolean>(false);

    // Document renaming state
    const [selectedFile, setSelectedFile] = useState<IngestFile | undefined>();
    const [newFileName, setNewFileName] = useState<string>('');

    // Document migration state
    const [selectedDuplicateFile, setSelectedDuplicateFile] = useState<IngestFile | undefined>();
    const [isMigrating, setIsMigrating] = useState<boolean>(false);
    const [migrationError, setMigrationError] = useState<string>('');

    // Migration options state
    const [checkHash, setCheckHash] = useState<boolean>(false);
    const [checkFileSize, setCheckFileSize] = useState<boolean>(true);
    const [mergeMostRecent, setMergeMostRecent] = useState<boolean>(false);
    const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);

    // Check if the new filename would be a duplicate
    const isDuplicateFileName = (newName: string): boolean => {
        if (!selectedFile || !newName.trim()) return false;
        const extension = getFileExtension(selectedFile.document_name);
        const fullNewName = newName.trim() + extension;
        return (filesByDocumentName.get(fullNewName) ?? []).some(file => file.id !== selectedFile.id);
    };

    // Check if the new filename is the same as the current filename
    const isSameAsCurrentName = (newName: string): boolean => {
        if (!selectedFile || !newName.trim()) return false;
        const currentNameWithoutExtension = getFileNameWithoutExtension(selectedFile.document_name);
        return newName.trim() === currentNameWithoutExtension;
    };

    // Get count of duplicates for a specific filename
    const getDuplicateCountForFile = (fileName: string): number => {
        return duplicateFilesByDocumentName.get(fileName)?.length ?? 0;
    };

    const getStatus = () => {
        if (!customer) return;
        store.backend!.getStorageMigrationStatus(customer.name, taskID).then((data: StorageMigrationStatus) => {
            setIsMigrated(data.migrated);
            setTaskID(data.task_id ?? undefined);
        });
    }

    useEffect(() => {
        if (customer) {
            setTaskID(undefined);
            setIsMigrated(undefined);
            getStatus();
        }
    }, [customer])

    useInterval(async () => {
        if (customer?.name && taskID) 
            getStatus()
    }, 2000);

    // Handle checkbox changes with mutual exclusivity
    const handleCheckHashChange = (checked: boolean) => {
        setCheckHash(checked);
        if (checked) {
            setMergeMostRecent(false);
        }
    };

    const handleCheckFileSizeChange = (checked: boolean) => {
        setCheckFileSize(checked);
        if (checked) {
            setMergeMostRecent(false);
        }
    };

    const handleMergeMostRecentChange = (checked: boolean) => {
        setMergeMostRecent(checked);
        if (checked) {
            setCheckHash(false);
            setCheckFileSize(false);
        }
    };

    const handleMigration = () => {
        // Check if all options are false and show warning
        if (!checkHash && !checkFileSize && !mergeMostRecent) {
            setShowWarningDialog(true);
            return;
        }
        
        setAborted(false);
        store.backend!.postRunScript(customer!.name, 'migrate_tenant', {
            user_email: user_email,
            check_hash: checkHash,
            check_file_size: checkFileSize,
            dups_by_recent: mergeMostRecent
        }).then((data: { task_id: string }) => { 
            setTaskID(data.task_id);
        });
    }

    const handleWarningConfirm = () => {
        setShowWarningDialog(false);
        setAborted(false);
        store.backend!.postRunScript(customer!.name, 'migrate_tenant', {
            user_email: user_email,
            check_hash: checkHash,
            check_file_size: checkFileSize,
            dups_by_recent: mergeMostRecent
        }).then((data: { task_id: string }) => { 
            setTaskID(data.task_id);
        });
    }

    const handleWarningCancel = () => {
        setShowWarningDialog(false);
    }

    const handleAbort = async () => {
        setAborted(true);
        if (!taskID) return;
        await store.backend!.postAbortTasks([taskID])
        store.backend!.getStorageMigrationStatus(customer!.name, taskID).then((data: StorageMigrationStatus) => {
            setIsMigrated(data.migrated);
            setTaskID(data.task_id ?? undefined);
        });
    }

    // Document renaming functionality
    const getFileExtension = (fileName: string): string => {
        const lastDotIndex = fileName.lastIndexOf('.');
        return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
    }

    const getFileNameWithoutExtension = (fileName: string): string => {
        const lastDotIndex = fileName.lastIndexOf('.');
        return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
    }

    const getTagDisplay = (file: IngestFile): string => {
        if (!file.tags || file.tags.length === 0) {
            return '[None]';
        }
        
        // Filter tags to only include those with active tag configs
        const validTags = file.tags.filter(tag => {
            const config = tagConfigs.find(config => config.tag_index === tag.tag_index);
            return config && config.tag_label === 'model';
        });
        
        if (validTags.length === 0) {
            return '[None]';
        } else if (validTags.length === 1) {
            return `[${validTags[0].value}]`;
        } else {
            // Show individual tag values from active configs
            const tagDisplayValues = validTags.map(tag => tag.value).join(', ');
            return `[${tagDisplayValues}]`;
        }
    };

    const handleRename = async () => {
        if (!selectedFile || !newFileName.trim()) return;
        
        const extension = getFileExtension(selectedFile.document_name);
        const fullNewName = newFileName.trim() + extension;
        
        try {
            // TODO: Add backend API call for renaming document
            // await store.backend!.renameDocument(customer!.name, selectedFile.id, fullNewName);
            console.log(`Renaming ${selectedFile.document_name} to ${fullNewName}`);
            
            // Reset form after successful rename
            setSelectedFile(undefined);
            setNewFileName('');
        } catch (error) {
            console.error('Failed to rename document:', error);
        }
    }

    // Create selection items for duplicate files
    const duplicateFileSelectionItems: SelectionItem[] = duplicateFiles.map(file => ({
        value: file.id,
        label: `${file.document_name} (ID: ${file.id}) ${getTagDisplay(file)}`
    }));

    const handleDuplicateFileSelection = (item?: SelectionItem) => {
        if (item) {
            const file = fileById.get(item.value);
            setSelectedDuplicateFile(file);
        } else {
            setSelectedDuplicateFile(undefined);
        }
    }

    const handleMigrate = async () => {
        if (!selectedDuplicateFile || !customer) return;
        
        setIsMigrating(true);
        setMigrationError('');
        
        try {
            const response = await store.backend!.migrateFilename(
                customer.name, 
                selectedDuplicateFile.document_name, 
                selectedDuplicateFile.id
            );
            
            // Check if the response indicates failure
            if (response && response.success === false) {
                setMigrationError(response.error || 'Migration failed');
                setIsMigrating(false);
                return;
            }
            
            // Clear selections before refreshing files
            setSelectedDuplicateFile(undefined);
            setSelectedFile(undefined);
            setNewFileName('');
            
            // Refresh files and update local state
            await store.refreshFiles();
        } catch (error) {
            console.error('Failed to migrate document:', error);
            setMigrationError('Failed to migrate document');
        } finally {
            setIsMigrating(false);
        }
    }

    return (
        <Stack direction="row" padding={2} height='100%' width='100%' spacing={0}>
            {/* Left Column - Existing Storage Migration Functionality */}
            <Stack padding={2} height='100%' width='50%' spacing={2}>
                <Stack direction={"row"} spacing={2}>
                    <Button variant='outlined'
                        style={{ width: '20%' }}
                        onClick={handleMigration}
                        disabled = {!customer || taskID !== undefined}
                    >
                        Migrate Documents
                    </Button>
                    <Button variant='outlined'
                        style={{ width: '20%' }}
                        onClick={handleAbort}
                        disabled = {!customer || taskID === undefined}
                    >
                        Abort Migration
                    </Button>
                </Stack>
                
                {/* Migration Options */}
                <Stack spacing={1}>
                    <Typography variant="subtitle2" gutterBottom>
                        Migration Options:
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={6}>
                        {/* Left side - Check options */}
                        <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Checkbox
                                    checked={checkHash}
                                    onChange={(e) => handleCheckHashChange(e.target.checked)}
                                    color="primary"
                                    disabled={!customer || taskID !== undefined}
                                />
                                <Typography variant="body2">Check Hash</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Checkbox
                                    checked={checkFileSize}
                                    onChange={(e) => handleCheckFileSizeChange(e.target.checked)}
                                    color="primary"
                                    disabled={!customer || taskID !== undefined}
                                />
                                <Typography variant="body2">Check Size</Typography>
                            </Stack>
                        </Stack>
                        
                        {/* Separator with OR text */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ width: '1px', height: '40px', backgroundColor: '#e0e0e0' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                OR
                            </Typography>
                            <Box sx={{ width: '1px', height: '40px', backgroundColor: '#e0e0e0' }} />
                        </Stack>
                        
                        {/* Right side - Merge option */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox
                                checked={mergeMostRecent}
                                onChange={(e) => handleMergeMostRecentChange(e.target.checked)}
                                color="primary"
                                disabled={!customer || taskID !== undefined}
                            />
                            <Typography variant="body2">Merge Docs by Most Recent</Typography>
                        </Stack>
                    </Stack>
                </Stack>
                
                <Box sx={{p: 2, backgroundColor: '#f5f5f5', borderRadius: 1,  border: '1px solid #ccc', color: '#333', height: '50%'}}>
                    {!customer ? 'Please select a tenant to view migration status.' :
                        aborted ? 'Migration has been aborted.' :
                        isMigrated ? 'Migration completed successfully for this tenant.' :
                        taskID ? `Migration in progress. Task ID: ${taskID}` :
                        'No active migration task has been found for this tenant.'}
                </Box>
            </Stack>

            {/* Divider */}
            <Box sx={{ width: '1px', backgroundColor: '#e0e0e0', mx: 1 }} />

            {/* Right Column - New Functionality */}
            <Stack padding={2} height='100%' width='50%' spacing={0}>
                {/* Top Section - Document Renaming (DISABLED) */}
                <Box sx={{p: 2, backgroundColor: '#f0f0f0', borderRadius: 1, border: '1px solid #e0e0e0', height: '50%', opacity: 0.5}}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#888' }}>
                        Rename Document (Disabled)
                    </Typography>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={[]}
                            getOptionLabel={() => ''}
                            onChange={() => {}}
                            disabled={true}
                            style={{ width: '100%' }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Feature temporarily disabled"
                                    variant="outlined"
                                    disabled={true}
                                />
                            )}
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                value=""
                                onChange={() => {}}
                                placeholder="Enter new name"
                                variant="outlined"
                                size="small"
                                style={{ flex: 1 }}
                                disabled={true}
                            />
                            <Typography variant="body2" sx={{ color: '#666', minWidth: 'fit-content' }}>
                            </Typography>
                        </Stack>
                        <Button
                            variant="outlined"
                            onClick={() => {}}
                            disabled={true}
                            style={{ width: 'fit-content' }}
                        >
                            Rename Document
                        </Button>
                    </Stack>
                </Box>
                
                {/* Horizontal Separator */}
                <Box sx={{ height: '1px', backgroundColor: '#e0e0e0', my: 1 }} />
                
                {/* Bottom Section - Document Migration */}
                <Box sx={{p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0', height: '50%'}}>
                    <Typography variant="h6" gutterBottom>
                        Migrate To Document
                    </Typography>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={duplicateFileSelectionItems}
                            getOptionLabel={(option) => option.label}
                            filterOptions={(options, { inputValue }) => {
                                if (!inputValue) return options;
                                const searchValue = inputValue.toLowerCase();
                                return options.filter(option => {
                                    // Search in the label (document name and tags)
                                    const labelMatch = option.label.toLowerCase().includes(searchValue);
                                    // Search in the document ID specifically
                                    const idMatch = option.value.toLowerCase().includes(searchValue);
                                    return labelMatch || idMatch;
                                });
                            }}
                            renderOption={(option) => {
                                const parts = option.label.split(/(\[.*?\])/);
                                return (
                                    <div>
                                        {parts.map((part, index) => 
                                            part.startsWith('[') && part.endsWith(']') ? (
                                                <strong key={index}>{part}</strong>
                                            ) : (
                                                <span key={index}>{part}</span>
                                            )
                                        )}
                                    </div>
                                );
                            }}
                            onChange={(event, value) => handleDuplicateFileSelection(value || undefined)}
                            disabled={!customer || duplicateFiles.length === 0 || isMigrating}
                            style={{ width: '100%' }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select a duplicate document to migrate (search by name or ID)"
                                    variant="outlined"
                                    style={{ backgroundColor: !customer || duplicateFiles.length === 0 || isMigrating ? undefined : 'white' }}
                                />
                            )}
                        />
                        {duplicateFiles.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                No duplicate documents found.
                            </Typography>
                        ) : selectedDuplicateFile ? (
                            <Typography variant="body2" color="textSecondary">
                                {getDuplicateCountForFile(selectedDuplicateFile.document_name)} found for "{selectedDuplicateFile.document_name}". Will migrate to file version found in model {getTagDisplay(selectedDuplicateFile)}.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                Found {duplicateFiles.length} documents with duplicate names.
                            </Typography>
                        )}
                        {migrationError && (
                            <Typography variant="body2" color="error">
                                {migrationError}
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            onClick={handleMigrate}
                            disabled={!selectedDuplicateFile || isMigrating}
                            style={{ width: 'fit-content' }}
                        >
                            {isMigrating ? 'Migrating...' : 'Migrate Document'}
                        </Button>
                    </Stack>
                </Box>
            </Stack>
            
            {/* Warning Dialog */}
            {showWarningDialog && (
                <StepConfirmation
                    confirmationDialogs={[
                        {
                            title: "Warning",
                            content: (
                                <Typography>
                                    Both "Check Hash" and "Check Size" options are disabled. This may result in incomplete migration validation. 
                                    Are you sure you want to proceed?
                                </Typography>
                            ),
                            onYes: handleWarningConfirm,
                            yesLabel: "Proceed Anyway",
                            onClose: handleWarningCancel,
                            closeLabel: "Cancel"
                        }
                    ]}
                    finish={handleWarningConfirm}
                    cancel={handleWarningCancel}
                    isValid={true}
                />
            )}
        </Stack>
    )
})
